import { useEffect, useState } from "react";

const SOURCES = {
  live: { label: "Live game stats", color: "#00FF66", pulse: true },
  simulated: { label: "Simulated feed · practice", color: "#FF9F0A", pulse: true },
  projected: { label: "Projected · kickoff pending", color: "#71717A", pulse: false },
};

export function UpdatedAgo({ at, testId = "live-updated-ago" }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!at) return null;
  const secs = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 1000));
  const text = secs < 60 ? `${secs}s ago` : `${Math.floor(secs / 60)}m ago`;
  return (
    <span className="text-white/40 text-[10px] uppercase tracking-widest" data-testid={testId}>
      Updated {text}
    </span>
  );
}

const RANK_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32", "#71717A", "#71717A"];

/** WHO'S WINNING — the live top of the position board, with your picks called out. */
export default function LiveTop5Board({ data, myPicks = [], rows = 8, testId = "live-top5-board" }) {
  const live = data?.live || {};
  const board = (live.board || []).slice(0, rows);
  if (!board.length) return null;

  const mine = new Set(myPicks);
  const src = SOURCES[live.source] || SOURCES.projected;
  const hits = myPicks.filter((pid) => board.slice(0, 5).some((b) => b.player_id === pid)).length;
  const pct = Math.round((live.progress || 0) * 100);

  return (
    <div className="border border-white/10 bg-[#141414]" data-testid={testId}>
      <div className="border-b border-white/10 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <div>
            <div className="text-white/40 text-xs uppercase tracking-widest mb-0.5">Right now</div>
            <div className="font-display text-2xl">WHO&apos;S WINNING</div>
          </div>
          <div className="text-right">
            <div
              className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5"
              style={{ color: src.color }}
              data-testid="live-source-badge"
            >
              {src.pulse && (
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: src.color }} />
              )}
              {src.label}
            </div>
            <div className="mt-0.5">
              <UpdatedAgo at={live.updated_at} />
            </div>
          </div>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#00FF66] transition-all duration-1000" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/40">
          <span>{pct}% of the slate played</span>
          {myPicks.length > 0 && (
            <span data-testid="live-hits-count" className={hits >= 3 ? "text-[#00FF66]" : "text-white/40"}>
              {hits} of your 5 in the top 5
            </span>
          )}
        </div>
      </div>

      <div className="divide-y divide-white/10">
        {board.map((p, i) => {
          const isMine = mine.has(p.player_id);
          const color = RANK_COLORS[i] || "#3f3f46";
          return (
            <div
              key={p.player_id}
              data-testid={`live-board-row-${i + 1}`}
              className={`flex items-center gap-3 p-3 ${isMine ? "bg-[#00FF66]/[0.07]" : ""} ${
                i === 4 ? "border-b-2 border-b-[#FFD700]/30" : ""
              }`}
            >
              <div
                className="w-8 h-8 rounded-sm border flex items-center justify-center font-display text-lg flex-shrink-0"
                style={{ borderColor: color, color }}
              >
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  {p.name}
                  {isMine && (
                    <span
                      className="text-[9px] uppercase tracking-widest text-[#00FF66] border border-[#00FF66]/40 px-1.5 py-0.5 rounded-sm"
                      data-testid={`your-pick-tag-${p.player_id}`}
                    >
                      Your pick
                    </span>
                  )}
                </div>
                <div className="text-white/40 text-xs uppercase tracking-widest">{p.team}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-white/40 text-[10px] uppercase tracking-widest">Fantasy</div>
                <div className="font-display text-xl" style={{ color: i < 5 ? "#00FF66" : "#71717A" }}>
                  {Number(p.points || 0).toFixed(1)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3 text-white/30 text-[10px] uppercase tracking-widest border-t border-white/10">
        Top 5 pays · line under 5th place is the cut
      </div>
    </div>
  );
}
